import React, { useState, useRef } from 'react';
import { Input, Button, message, Col } from 'antd';
import type { InputRef } from 'antd';
import './OtpForm.less';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store/store';
import useMembershipAuthHelpers from '../../../hooks/useMembershipAuthHelpers';
import {
  setIsOtpAuthCheckModalOpen,
  setMembershipUser,
} from '../../../store/config';
import SpinLoader from '@skygroup/shared/components/spin-loader/SpinLoader';
import ModelApiMessage, {
  ApiStatus,
  ApiStatusMessage,
} from '@skygroup/shared/components/crud-helpers/ModelApiMessage';
import { OtpMode } from '../../../store/config';

interface OTPFormProps {
  otpMode: OtpMode;
  setIsLoginSuccess: (success: boolean) => void;
  sessionId: string;
}

const columnSize = { xs: 20, md: 35 };

const formatExpirationDate = (date: Date) => {
  const pad = (value: number) => value.toString().padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const OTPForm: React.FC<OTPFormProps> = ({
  otpMode,
  setIsLoginSuccess,
  sessionId,
}) => {
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { auth: t } = useSelector(
    (state: RootState) => state.config.translations
  );
  const { verifyOTPFn } = useMembershipAuthHelpers();
  const [apiStatusMessage, setApiStatusMessage] = useState(
    {} as ApiStatusMessage
  );
  const inputRefs = useRef<Array<InputRef | null>>([]);

  const handleChange = (value: string, index: number) => {
    if (/^[0-9]$/.test(value) || value === '') {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < otp.length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
      const prevIndex = index - 1;
      setOtp((prev) => {
        const newOtp = [...prev];
        newOtp[prevIndex] = '';
        return newOtp;
      });
      setTimeout(() => {
        inputRefs.current[prevIndex]?.focus();
      }, 0);
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number
  ) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtp(digits);
      inputRefs.current[otp.length - 1]?.focus();
    } else {
      const digit = pastedData.charAt(0);
      if (/^\d$/.test(digit)) {
        handleChange(digit, index);
      }
    }
  };

  const handleSubmit = async () => {
    const otpString = otp.join('');
    if (otpString.length === otp.length) {
      setLoading(true);
      try {
        const membershipUserWithExpiration = await verifyOTPFn(
          sessionId,
          otpString,
          otpMode
        );

        let membershipUserData = membershipUserWithExpiration;
        let user_name = membershipUserWithExpiration.user_name;

        if (otpMode == OtpMode.MEMBERSHIP) {
          const { auth_type, expiration_date, ...rest } = membershipUserWithExpiration.data;
          membershipUserData = {
            auth_type,
            data: rest,
            expiration_date,
          };
          user_name = `${rest.first_name} ${rest.last_name}`;
        } else if (otpMode == OtpMode.ORG_ISTADRUT) {
          membershipUserData.auth_type = OtpMode.ORG_ISTADRUT;
          // Using milliseconds (JavaScript Date works with milliseconds, not seconds)
          const EXPIRATION_DAYS_MS = 14 * 24 * 60 * 60 * 1000; // 14 days in milliseconds
          // Create expiration date in "YYYY-MM-DD HH:mm:ss" format
          const expirationDate = new Date(Date.now() + EXPIRATION_DAYS_MS);
          membershipUserData.expiration_date = formatExpirationDate(
            expirationDate
          );
        }

        localStorage.setItem(
          'membershipUser',
          JSON.stringify(membershipUserData)
        );

        dispatch(setMembershipUser(membershipUserData));
        dispatch(setIsOtpAuthCheckModalOpen(false));
        message.success(`${user_name} ${t.loginSuccess}`);
      } catch {
        setApiStatusMessage({
          status: ApiStatus.error,
          data: { message: t.otpError },
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStartOver = () => {
    setIsLoginSuccess(false);
    setOtp(['', '', '', '', '', '']);
    inputRefs.current[0]?.focus();
  };

  return (
    <SpinLoader isLoading={loading} size="large" onTop={true}>
      <div className="otp-form">
        <div className="otp-inputs">
          {otp.map((digit, index) => (
            <Input
              key={index}
              value={digit}
              maxLength={1}
              className="otp-input"
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={(e) => handlePaste(e, index)}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
            />
          ))}
        </div>
        <Button
          type="primary"
          onClick={handleSubmit}
          block
          style={{ marginTop: 16 }}
          className="btn-style-5"
        >
          {t.verify}
        </Button>
        <Col {...columnSize} offset={1}>
          <ModelApiMessage apiStatusMessage={apiStatusMessage} t={t.crud} />
        </Col>
        <div className="otp-start-over">
          <span onClick={handleStartOver}>{t.otpStartOver}</span>
        </div>
      </div>
    </SpinLoader>
  );
};

export default OTPForm;
